import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { showAPI, default as api } from '../utils/api';
import { Plus, Trash, DotsSixVertical, CaretDown } from '@phosphor-icons/react';
import { TshirtBlock, TieOutBlock, ShavingsBlock } from '../components/show/ShowMerchBlocks';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChartBuilder, FacilityMapUpload, FacilityTemplatePanel } from '../components/dashboard/ChartBuilder';
import { AdditionalChartsSection } from '../components/dashboard/AdditionalChartsSection';
import { ExtraLocationsEditor } from '../components/dashboard/ExtraLocationsEditor';
import { ProducerContactCard } from '../components/show/ProducerContactCard';
import { TransferShowModal } from '../components/show/TransferShowModal';
import { ShowSquareRoutingPanel } from '../components/dashboard/ShowSquareRoutingPanel';
import { CoSanctioningSection } from '../components/CoSanctioningSection';
import { useAuth } from '../contexts/AuthContext';

// Platform fee dropdown options: 1.0% → 8.0% in 0.5% increments. Matches
// the master-admin settings page limits to prevent accidental typos.
const FEE_OPTIONS = (() => {
  const arr = [0];
  for (let p = 1.0; p <= 8.0 + 1e-9; p += 0.5) {
    arr.push(Math.round(p * 10) / 10);
  }
  return arr;
})();

const SortableClassCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children(listeners)}
    </div>
  );
};

const CollapsibleSection = ({ id, title, subtitle, accent, badge, defaultOpen, children }) => {
  const storageKey = `showedit-section-${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached === '1') return true;
      if (cached === '0') return false;
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
    return defaultOpen !== false;
  });
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid={`section-${id}`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={`section-${id}-toggle`}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 hover:bg-[#0A0A0A]/40 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{title}</h2>
            {badge}
          </div>
          {subtitle && <p className="text-xs text-[#9E9E9E] mt-1">{subtitle}</p>}
        </div>
        <CaretDown
          size={20}
          weight="bold"
          className={`text-[#9E9E9E] flex-shrink-0 transition-transform ${open ? '' : '-rotate-90'}`}
          style={accent ? { color: accent } : undefined}
        />
      </button>
      {open && (
        <div className="px-6 pb-6 pt-2 border-t border-[#262626]/40" data-testid={`section-${id}-body`}>
          {children}
        </div>
      )}
    </div>
  );
};

const ShowEdit = () => {
  const navigate = useNavigate();
  const { id: showId } = useParams();
  const { user } = useAuth();
  const userRoles = useMemo(() => user?.roles || (user?.role ? [user.role] : []), [user]);
  const isMaster = userRoles.some(r => ['MASTER', 'LMASTER'].includes(r));
  const [formData, setFormData] = useState({
    name: '', description: '', start_date: '', end_date: '', location: '',
    arenas: ['Main Arena'], stalls_available: 0, stall_price: 0,
    stall_price_per_day: 0,
    stall_per_day_disabled: false,
    online_entries_disabled: false,
    entry_form_header_note: '',
    entry_form_footer_note: '',
    entry_form_release_text: '',
    rv_hookups_available: 0, rv_hookup_price: 0,
    rv_hookup_price_per_day: 0,
    video_stream_url: '',
    refund_cutoff_date: '',
    entry_deadline: '',
    processing_fee: 0,
    processing_fee_type: 'per_day',
    locked_days: [],
    service_fee_pct: '',
    producer_contact_name: '',
    producer_contact_email: '',
    producer_contact_phones: [],
    check_payable_to: '',
    check_mailing_address: '',
    tshirt_enabled: false,
    tshirt_price: 0,
    tshirt_label: 'Show T-Shirt',
    tshirt_sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    tie_out_fee_enabled: false,
    tie_out_fee_price: 0,
    tie_out_fee_label: 'Tie-Out Fee',
    shavings_enabled: false,
    shavings_price_per_bag: 0,
    shavings_label: 'Shavings'
  });
  const [classes, setClasses] = useState([]);
  const [sidepots, setSidepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeEditDayTab, setActiveEditDayTab] = useState('ALL');
  const [showbillUrl, setShowbillUrl] = useState(null);
  const [ownerOrgId, setOwnerOrgId] = useState('');
  // Square routing flags captured from the loaded show for the
  // ShowSquareRoutingPanel below. Saved independently of the main form.
  const [squareRouted, setSquareRouted] = useState(false);
  const [squareMerchantId, setSquareMerchantId] = useState(null);
  // Admin "Transfer Show" modal — reassigns show ownership to another
  // producer so payment splits route to their connected Stripe account.
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [showMeta, setShowMeta] = useState({ name: '', currentOwnerName: '' });

  useEffect(() => { loadShow(); }, [showId]);

  const loadShow = async () => {
    try {
      const { data: show } = await showAPI.get(showId);
      setFormData({
        name: show.name, description: show.description || '',
        start_date: show.start_date, end_date: show.end_date,
        location: show.location, arenas: show.arenas || ['Main Arena'],
        stalls_available: show.stalls_available || 0, stall_price: show.stall_price || 0,
        stall_price_per_day: show.stall_price_per_day || 0,
        stall_per_day_disabled: !!show.stall_per_day_disabled,
        online_entries_disabled: !!show.online_entries_disabled,
        entry_form_header_note: show.entry_form_header_note || '',
        entry_form_footer_note: show.entry_form_footer_note || '',
        entry_form_release_text: show.entry_form_release_text || '',
        rv_hookups_available: show.rv_hookups_available || 0, rv_hookup_price: show.rv_hookup_price || 0,
        rv_hookup_price_per_day: show.rv_hookup_price_per_day || 0,
        video_stream_url: show.video_stream_url || '',
        refund_cutoff_date: show.refund_cutoff_date || '',
        entry_deadline: show.entry_deadline || '',
        processing_fee: show.processing_fee || 0,
        processing_fee_type: show.processing_fee_type || 'per_day',
        locked_days: show.locked_days || [],
        service_fee_pct: show.service_fee_pct ?? '',
        producer_contact_name: show.producer_contact_name || '',
        producer_contact_email: show.producer_contact_email || '',
        producer_contact_phones: show.producer_contact_phones || [],
        check_payable_to: show.check_payable_to || '',
        check_mailing_address: show.check_mailing_address || '',
        tshirt_enabled: show.tshirt_enabled || false,
        tshirt_price: show.tshirt_price || 0,
        tshirt_label: show.tshirt_label || 'Show T-Shirt',
        tshirt_sizes: show.tshirt_sizes && show.tshirt_sizes.length ? show.tshirt_sizes : ['S', 'M', 'L', 'XL', 'XXL'],
        tie_out_fee_enabled: show.tie_out_fee_enabled || false,
        tie_out_fee_price: show.tie_out_fee_price || 0,
        tie_out_fee_label: show.tie_out_fee_label || 'Tie-Out Fee',
        shavings_enabled: show.shavings_enabled || false,
        shavings_price_per_bag: show.shavings_price_per_bag || 0,
        shavings_label: show.shavings_label || 'Shavings'
      });
      setClasses(show.classes.map(c => ({
        id: c.id, name: c.name, entry_fee: c.entry_fee,
        payback_percent: c.payback_percent || 70, added_money: c.added_money || 0,
        scheduled_day: c.scheduled_day || '', scheduled_date: c.scheduled_date || '',
        division_type: c.division_type || 'NONE', division_split: c.division_split || 0.5,
        division_splits: (c.division_type === '3D' || c.division_type === '4D') && Array.isArray(c.division_splits) ? c.division_splits.map(v => Number(v) || 0) : null,
        arena: c.arena || '',
        entry_cap: c.entry_cap || '',
        drag_size: c.drag_size ?? '',
        draw_reversed: !!c.draw_reversed,
        class_type: c.class_type || 'standard',
        multi_go_group: c.multi_go_group || '',
        go_number: c.go_number ?? null,
        is_finals: !!c.is_finals,
        qualification_rule: c.qualification_rule || null,
        rollover_from_class_ids: c.rollover_from_class_ids || (c.rollover_from_class_id ? [c.rollover_from_class_id] : []),
        rollover_category: c.rollover_category || '',
        // v261 — Finals classes can base their pool on another class's
        // entry count (e.g. Go 1) since the finals entry money was
        // pre-allocated from Go 1 / Go 2 registrations.
        entry_count_source_class_id: c.entry_count_source_class_id || '',
        points_enabled: c.points_enabled === undefined ? true : !!c.points_enabled,
        points_mode: c.points_mode || (c.points_enabled === false ? 'OFF' : 'DIVISIONS'),
      })));
      setSidepots((show.sidepots || []).map(s => ({
        id: s.id, name: s.name, entry_fee: s.entry_fee, rollover_from_class_ids: s.rollover_from_class_ids || [], division_type: s.division_type || 'NONE', division_split: s.division_split ?? 0.5, payout_pct: s.payout_pct ?? 100, added_money: s.added_money ?? 0, scheduled_day: s.scheduled_day || ''
      })));
      setShowbillUrl(show.showbill_url || null);
      setHasCustomEntryForm(!!show.has_custom_entry_form);
      setOwnerOrgId(show.organization_id || '');
      setSquareRouted(!!show.square_routing_enabled);
      setSquareMerchantId(show.square_merchant_id || null);
      // Capture meta for the Transfer Show modal title (admin feature).
      setShowMeta({
        name: show.name || '',
        currentOwnerName: show.producer_contact_name || show.created_by_name || '',
      });
    } catch { toast.error('Failed to load show'); navigate('/dashboard'); }
    finally { setLoading(false); }
  };

  const getShowDays = () => {
    if (!formData.start_date || !formData.end_date || formData.start_date === formData.end_date) return [];
    const days = [];
    const [sy, sm, sd] = formData.start_date.split('-').map(Number);
    const [ey, em, ed] = formData.end_date.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const current = new Date(start);
    while (current <= end) {
      const dayName = current.toLocaleDateString('en-US', { weekday: 'long' });
      const label = `${dayName} (${current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
      // ISO date string for backend `locked_days` comparison with class.scheduled_date.
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      days.push({ value: dayName, label, date: `${yyyy}-${mm}-${dd}` });
      current.setDate(current.getDate() + 1);
    }
    return days;
  };
  const showDays = getShowDays();
  const isMultiDay = showDays.length > 0;

  const addClass = (dayOverride) => {
    const assignDay = dayOverride || (activeEditDayTab !== 'ALL' ? activeEditDayTab : '');
    setClasses([...classes, {
      name: '', entry_fee: '', payback_percent: 70, added_money: 0,
      scheduled_day: assignDay, scheduled_date: '', division_type: 'NONE',
      division_split: 0.5, arena: formData.arenas[0] || '',
      entry_cap: '',
      drag_size: '',
      draw_reversed: false,
      class_type: 'standard',
      rollover_from_class_ids: [], rollover_category: '',
      points_enabled: true,
      points_mode: 'DIVISIONS',
    }]);
  };

  const removeClass = (index) => {
    const removedId = classes[index]?.id;
    const updated = classes.filter((_, i) => i !== index).map(c => ({
      ...c,
      rollover_from_class_ids: (c.rollover_from_class_ids || []).filter(id => id !== removedId)
    }));
    setClasses(updated);
  };

  /**
   * Duplicate a class so the producer can reuse payouts/fees and just rename it.
   * Strips the server-side id (so the new class is saved as a new doc),
   * appends " (Copy)" to the name, and clears any rollover targets that pointed
   * to the original — rollovers should be configured fresh on the copy.
   */
  const duplicateClass = (index) => {
    const source = classes[index];
    if (!source) return;
    const { id: _omit, ...rest } = source;
    const copy = {
      ...rest,
      name: source.name ? `${source.name} (Copy)` : 'New Class (Copy)',
      rollover_from_class_ids: [],
      rollover_category: '',
    };
    // Insert directly after the source so producers don't lose their place
    const next = [...classes.slice(0, index + 1), copy, ...classes.slice(index + 1)];
    setClasses(next);
  };

  const updateClass = (index, field, value) => {
    const updated = [...classes];
    updated[index] = { ...updated[index], [field]: value };
    setClasses(updated);
  };

  const moveClass = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= classes.length) return;
    const updated = [...classes];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setClasses(updated);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const getClassDndId = (cls, index) => cls.id || `cls_idx_${index}`;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setClasses(prev => {
      const oldIndex = prev.findIndex((c, i) => getClassDndId(c, i) === active.id);
      const newIndex = prev.findIndex((c, i) => getClassDndId(c, i) === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const addSidepot = () => setSidepots([...sidepots, { name: '', entry_fee: '', rollover_from_class_ids: [], division_type: 'NONE', division_split: 0.5, payout_pct: 100, added_money: 0, scheduled_day: '' }]);
  const removeSidepot = (index) => setSidepots(sidepots.filter((_, i) => i !== index));
  const updateSidepot = (index, field, value) => {
    const updated = [...sidepots];
    updated[index] = { ...updated[index], [field]: value };
    setSidepots(updated);
  };

  const handleShowbillUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      const { data } = await import('../utils/api').then(m => m.default.post(`/shows/${showId}/showbill`, formDataObj, { headers: { 'Content-Type': 'multipart/form-data' } }));
      setShowbillUrl(data.showbill_url);
      toast.success('Showbill uploaded!');
    } catch { toast.error('Failed to upload showbill'); }
    e.target.value = '';
  };

  const handleShowbillDelete = async () => {
    if (!showbillUrl) return;
    if (!window.confirm('Remove the current showbill image from this show?')) return;
    try {
      const api = (await import('../utils/api')).default;
      await api.delete(`/shows/${showId}/showbill`);
      setShowbillUrl(null);
      toast.success('Showbill removed');
    } catch { toast.error('Failed to remove showbill'); }
  };

  // Custom mail-in entry form (v214): producer uploads their own PDF
  // that OVERRIDES the auto-generated form. Same upload pattern as the
  // showbill image so producers can swap in a polished file at any time.
  const [hasCustomEntryForm, setHasCustomEntryForm] = useState(false);
  const [uploadingEntryForm, setUploadingEntryForm] = useState(false);

  const handleCustomEntryFormUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingEntryForm(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const api = (await import('../utils/api')).default;
      await api.post(`/shows/${showId}/custom-entry-form`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHasCustomEntryForm(true);
      toast.success('Custom entry form uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload entry form');
    } finally {
      setUploadingEntryForm(false);
      e.target.value = '';
    }
  };

  const handleCustomEntryFormDelete = async () => {
    if (!hasCustomEntryForm) return;
    if (!window.confirm('Remove your custom entry form? The auto-generated form will be used instead.')) return;
    try {
      const api = (await import('../utils/api')).default;
      await api.delete(`/shows/${showId}/custom-entry-form`);
      setHasCustomEntryForm(false);
      toast.success('Custom entry form removed');
    } catch { toast.error('Failed to remove custom entry form'); }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const showData = {
        ...formData,
        showbill_url: showbillUrl || null,
        classes: classes.map(c => ({
          ...(c.id ? { id: c.id } : {}),
          name: c.name, entry_fee: parseFloat(c.entry_fee),
          payback_percent: parseFloat(c.payback_percent),
          added_money: parseFloat(c.added_money) || 0,
          scheduled_day: c.scheduled_day || '', scheduled_date: c.scheduled_date || '',
          division_type: c.division_type || 'NONE',
          division_split: c.division_type === '2D' ? parseFloat(c.division_split) : null,
          division_splits: (() => {
            // v244 — only include splits for 3D/4D. Guarantee correct
            // length (fall back to standard defaults) so a 3D→4D switch
            // doesn't send a stale 3-element array to the backend.
            if (c.division_type !== '3D' && c.division_type !== '4D') return null;
            const expected = c.division_type === '4D' ? 4 : 3;
            const defaults = c.division_type === '4D' ? [0, 0.5, 1.0, 2.0] : [0, 1.0, 2.0];
            if (!Array.isArray(c.division_splits) || c.division_splits.length !== expected) return defaults;
            const cleaned = c.division_splits.map(v => Number(v) || 0);
            cleaned[0] = 0;
            return cleaned;
          })(),
          arena: c.arena || formData.arenas[0] || '',
          rollover_from_class_ids: c.rollover_from_class_ids || [],
          rollover_category: c.rollover_category || null,
          entry_cap: (c.entry_cap === '' || c.entry_cap == null) ? 0 : (parseInt(c.entry_cap, 10) || 0),
          // v248 — drag_size = announcer's drag width; empty/0 = no gap.
          // draw_reversed is set by the reverse-draw endpoint, but we
          // still forward the current value so a full class save doesn't
          // silently clear it.
          drag_size: (c.drag_size === '' || c.drag_size == null) ? 0 : (parseInt(c.drag_size, 10) || 0),
          draw_reversed: !!c.draw_reversed,
          class_type: c.class_type || 'standard',
          multi_go_group: (c.multi_go_group || '').trim() || null,
          go_number: c.go_number ? parseInt(c.go_number, 10) : null,
          is_finals: !!c.is_finals,
          qualification_rule: c.is_finals ? (c.qualification_rule || null) : null,
          // v261 — Nullable pointer to another class whose entry count
          // seeds this class's payout pool. Empty string means "use my
          // own entries" (default behavior).
          entry_count_source_class_id: c.entry_count_source_class_id || null,
          points_enabled: c.points_enabled === undefined ? true : !!c.points_enabled,
          points_mode: c.points_mode || (c.points_enabled === false ? 'OFF' : 'DIVISIONS'),
        })),
        sidepots: sidepots.map(s => ({
          ...(s.id ? { id: s.id } : {}),
          name: s.name,
          entry_fee: parseFloat(s.entry_fee),
          rollover_from_class_ids: s.rollover_from_class_ids || [],
          division_type: s.division_type || 'NONE',
          division_split: s.division_type === '2D' ? (parseFloat(s.division_split) || 0.5) : null,
          payout_pct: parseFloat(s.payout_pct ?? 100),
          added_money: parseFloat(s.added_money ?? 0),
          scheduled_day: s.scheduled_day || '',
        })),
        stalls_available: parseInt(formData.stalls_available) || 0,
        stall_price: parseFloat(formData.stall_price) || 0,
        stall_price_per_day: parseFloat(formData.stall_price_per_day) || 0,
        stall_per_day_disabled: !!formData.stall_per_day_disabled,
        online_entries_disabled: !!formData.online_entries_disabled,
        entry_form_header_note: formData.entry_form_header_note || null,
        entry_form_footer_note: formData.entry_form_footer_note || null,
        entry_form_release_text: formData.entry_form_release_text || null,
        rv_hookups_available: parseInt(formData.rv_hookups_available) || 0,
        rv_hookup_price: parseFloat(formData.rv_hookup_price) || 0,
        rv_hookup_price_per_day: parseFloat(formData.rv_hookup_price_per_day) || 0,
        refund_cutoff_date: formData.refund_cutoff_date || null,
        entry_deadline: formData.entry_deadline || null,
        producer_contact_name: formData.producer_contact_name || null,
        producer_contact_email: formData.producer_contact_email || null,
        producer_contact_phones: formData.producer_contact_phones || [],
        check_mailing_address: formData.check_mailing_address || null,
        tshirt_enabled: !!formData.tshirt_enabled,
        tshirt_price: Number(formData.tshirt_price) || 0,
        tshirt_label: formData.tshirt_label || 'Show T-Shirt',
        tshirt_sizes: formData.tshirt_sizes || [],
        tie_out_fee_enabled: !!formData.tie_out_fee_enabled,
        tie_out_fee_price: Number(formData.tie_out_fee_price) || 0,
        tie_out_fee_label: formData.tie_out_fee_label || 'Tie-Out Fee',
        shavings_enabled: !!formData.shavings_enabled,
        shavings_price_per_bag: Number(formData.shavings_price_per_bag) || 0,
        shavings_label: formData.shavings_label || 'Shavings'
      };
      // Per-show service-fee override — only platform admins can send this.
      if (isMaster) {
        showData.service_fee_pct = formData.service_fee_pct === '' || formData.service_fee_pct === null
          ? null
          : parseFloat(formData.service_fee_pct);
      }
      await showAPI.update(showId, showData);
      toast.success('Show updated successfully!');
      navigate(`/shows/${showId}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update show'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="shimmer h-12 w-12 rounded-full"></div></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}} data-testid="edit-show-title">Edit Show</h1>
            <p className="text-[#9E9E9E]">Update pricing, classes, and show details</p>
          </div>
          {isMaster && (
            <button
              type="button"
              onClick={() => setTransferModalOpen(true)}
              className="bg-[#5E5CE6] hover:bg-[#5E5CE6]/90 text-white font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-md flex items-center gap-1.5 transition-colors"
              data-testid="transfer-show-btn"
            >
              <span aria-hidden>⇄</span> Transfer Show
            </button>
          )}
        </div>

        {transferModalOpen && (
          <TransferShowModal
            showId={showId}
            showName={showMeta.name || formData.name}
            currentOwnerName={showMeta.currentOwnerName}
            onClose={() => setTransferModalOpen(false)}
            onTransferred={() => {
              // After transfer, the show no longer belongs to the current user
              // (unless they were the target). Send them back to the dashboard.
              navigate('/dashboard');
            }}
          />
        )}

        <form onSubmit={handleSubmit} data-testid="edit-show-form" className="space-y-8">
          {/* Basic Info */}
          <CollapsibleSection id="basic-info" title="Basic Information" defaultOpen>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Show Name</label>
                <input data-testid="edit-show-name" type="text" required value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Description</label>
                <textarea data-testid="edit-show-description" value={formData.description} rows={3}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Start Date</label>
                  <input data-testid="edit-show-start-date" type="date" required value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">End Date</label>
                  <input data-testid="edit-show-end-date" type="date" required value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Location</label>
                <input data-testid="edit-show-location" type="text" required value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors" />
              </div>
              {/* Arenas */}
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Arenas</label>
                <div className="space-y-2">
                  {formData.arenas.map((arena, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input type="text" value={arena} data-testid={`edit-arena-input-${idx}`}
                        onChange={(e) => { const u = [...formData.arenas]; u[idx] = e.target.value; setFormData({...formData, arenas: u}); }}
                        className="flex-1 px-4 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors" />
                      {formData.arenas.length > 1 && (
                        <button type="button" onClick={() => setFormData({...formData, arenas: formData.arenas.filter((_, i) => i !== idx)})} className="px-3 py-2 text-[#FF3B30] hover:text-[#FF3B30]/80">
                          <Trash size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" data-testid="edit-add-arena-btn"
                    onClick={() => setFormData({...formData, arenas: [...formData.arenas, `Arena ${formData.arenas.length + 1}`]})}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#0096FF] hover:text-[#00D4FF] transition-colors">
                    <Plus size={14} weight="bold" /> Add Arena
                  </button>
                </div>
              </div>
              {/* Refund Cutoff Date */}
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Refund Cutoff Date</label>
                <input data-testid="edit-refund-cutoff" type="date" value={formData.refund_cutoff_date}
                  onChange={(e) => setFormData({...formData, refund_cutoff_date: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                <p className="text-xs text-[#9E9E9E] mt-1">Riders can request refunds before this date. Defaults to the show start date if not set.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Online Entry Deadline</label>
                <input data-testid="edit-entry-deadline" type="datetime-local" value={formData.entry_deadline}
                  onChange={(e) => setFormData({...formData, entry_deadline: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                <p className="text-xs text-[#9E9E9E] mt-1">Online entries will close at this date and time. Leave blank for no deadline.</p>
                <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="edit-online-entries-disabled-label">
                  <input
                    type="checkbox"
                    checked={!!formData.online_entries_disabled}
                    onChange={(e) => setFormData({...formData, online_entries_disabled: e.target.checked})}
                    className="w-3.5 h-3.5 accent-[#FF9500]"
                    data-testid="edit-online-entries-disabled"
                  />
                  <span className="text-[11px] text-[#9E9E9E]">
                    Disable online entries — riders must download the printable form and mail it in
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Office/Rider Fee ($)</label>
                <input data-testid="edit-processing-fee" type="number" step="0.01" min="0" value={formData.processing_fee}
                  onChange={(e) => setFormData({...formData, processing_fee: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                  placeholder="0.00" />
                <p className="text-xs text-[#9E9E9E] mt-1">Flat fee added per entry transaction. Leave at 0 for no office/rider fee.</p>
                <div className="mt-3 flex gap-2" data-testid="edit-processing-fee-type-toggle">
                  <button type="button" onClick={() => setFormData({...formData, processing_fee_type: 'weekend'})}
                    className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-colors ${(formData.processing_fee_type || 'per_day') === 'weekend' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:border-[#0096FF]/50'}`}
                    data-testid="edit-processing-fee-type-weekend">
                    Weekend (flat, once per rider)
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, processing_fee_type: 'per_day'})}
                    className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-colors ${(formData.processing_fee_type || 'per_day') === 'per_day' ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:border-[#0096FF]/50'}`}
                    data-testid="edit-processing-fee-type-per-day">
                    Per Day
                  </button>
                </div>
                <p className="text-[10px] text-[#555] mt-1">Weekend: charged once per rider regardless of how many days/horses they enter. Per Day: charged once per rider per show day.</p>
              </div>
              {isMaster && (
                <div data-testid="edit-service-fee-pct-container">
                  <label className="block text-sm font-medium text-[#0096FF] mb-2">
                    Service Fee Override (Platform Admin Only)
                  </label>
                  <select
                    data-testid="edit-service-fee-pct"
                    value={formData.service_fee_pct === '' || formData.service_fee_pct === null ? '' : String(formData.service_fee_pct)}
                    onChange={(e) => setFormData({ ...formData, service_fee_pct: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#0096FF]/30 rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                  >
                    <option value="">Use platform default</option>
                    {FEE_OPTIONS.map((pctOption) => {
                      const val = pctOption / 100;
                      let label;
                      if (pctOption === 0) label = '0% (Waived)';
                      else if (pctOption % 1 === 0) label = `${pctOption}%`;
                      else label = `${pctOption.toFixed(1)}%`;
                      return <option key={pctOption} value={val}>{label}</option>;
                    })}
                  </select>
                  <p className="text-xs text-[#9E9E9E] mt-1">
                    Overrides the platform-wide service fee for this show only. Leave as
                    &quot;Use platform default&quot; to follow the global setting.
                  </p>
                </div>
              )}
              {/* T-Shirt merch — moved here per user request */}
              <TshirtBlock formData={formData} setFormData={setFormData} testPrefix="edit" />
            </div>
          </CollapsibleSection>

          {/* Producer Contact */}
          <ProducerContactCard
            value={formData}
            onChange={(patch) => setFormData({ ...formData, ...patch })}
            testIdPrefix="edit-producer-contact"
          />

          {/* Square Payment Routing — saves immediately (separate from main form) */}
          <ShowSquareRoutingPanel
            showId={showId}
            initialRouted={squareRouted}
            initialMerchantId={squareMerchantId}
          />

          {/* Showbill Image */}
          <CollapsibleSection id="showbill" title="Showbill Image" subtitle="Flyer image shown on listings and the show page" defaultOpen={false}>
            <p className="text-sm text-[#9E9E9E] mb-4">Upload a showbill image or flyer. This will be displayed on the show listing and detail pages.</p>
            {showbillUrl && (
              <div className="mb-4 rounded-lg overflow-hidden border border-[#262626] bg-[#0A0A0A]">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${showbillUrl}`} alt="Current showbill" className="w-full max-h-48 object-contain" data-testid="edit-showbill-preview" />
              </div>
            )}
            <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={handleShowbillUpload} className="hidden" id="edit-showbill-upload" data-testid="edit-showbill-input" />
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="edit-showbill-upload" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors cursor-pointer text-sm" data-testid="edit-showbill-btn">
                {showbillUrl ? 'Replace Showbill' : 'Upload Showbill'}
              </label>
              {showbillUrl && (
                <button
                  type="button"
                  onClick={handleShowbillDelete}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/40 hover:bg-[#FF3B30]/20 text-[#FF3B30] font-semibold rounded-md transition-colors text-sm"
                  data-testid="edit-showbill-delete-btn"
                >
                  Remove Showbill
                </button>
              )}
            </div>
          </CollapsibleSection>

          {/* Merch & Add-on Fees — moved into Basic Info and Stalls/RV sections */}

          {/* Accommodations */}
          <CollapsibleSection id="accommodations" title="Stalls & RV Hookups" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Stalls Available</label>
                <input data-testid="edit-stalls-available" type="number" min="0" value={formData.stalls_available}
                  onChange={(e) => setFormData({...formData, stalls_available: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Stall Price - Weekend ($)</label>
                <input data-testid="edit-stall-price" type="number" min="0" step="0.01" value={formData.stall_price}
                  onChange={(e) => setFormData({...formData, stall_price: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Stall Price - Per Day ($)</label>
                <input data-testid="edit-stall-price-perday" type="number" min="0" step="0.01" value={formData.stall_price_per_day}
                  onChange={(e) => setFormData({...formData, stall_price_per_day: e.target.value})}
                  disabled={!!formData.stall_per_day_disabled}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors disabled:opacity-40" />
                <p className="text-[10px] text-[#555] mt-1">Set both to let riders choose</p>
                <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="edit-stall-per-day-disabled-label">
                  <input type="checkbox"
                    checked={!!formData.stall_per_day_disabled}
                    onChange={(e) => setFormData({...formData, stall_per_day_disabled: e.target.checked})}
                    className="w-3.5 h-3.5 accent-[#FF9500]"
                    data-testid="edit-stall-per-day-disabled" />
                  <span className="text-[11px] text-[#9E9E9E]">Disable per-day stall pricing (riders see weekend rate only)</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">RV Hookups</label>
                <input data-testid="edit-rv-available" type="number" min="0" value={formData.rv_hookups_available}
                  onChange={(e) => setFormData({...formData, rv_hookups_available: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">RV Hookup Price - Weekend ($)</label>
                <input data-testid="edit-rv-price" type="number" min="0" step="0.01" value={formData.rv_hookup_price}
                  onChange={(e) => setFormData({...formData, rv_hookup_price: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#F8F8F8] mb-2">RV Hookup Price - Per Day ($)</label>
                <input data-testid="edit-rv-price-perday" type="number" min="0" step="0.01" value={formData.rv_hookup_price_per_day}
                  onChange={(e) => setFormData({...formData, rv_hookup_price_per_day: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                <p className="text-[10px] text-[#555] mt-1">Set both to let riders choose</p>
              </div>
            </div>

            {/* Tie-Out Fee — sits right below stall/RV pricing per user request */}
            <TieOutBlock formData={formData} setFormData={setFormData} testPrefix="edit" />

            {/* Shavings/Sawdust — sold by the bag */}
            <ShavingsBlock formData={formData} setFormData={setFormData} testPrefix="edit" />

            {/* Chart Builders */}
            <div className="space-y-4 mt-6">
              <FacilityTemplatePanel showId={showId} />
              <FacilityMapUpload showId={showId} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <ChartBuilder showId={showId} type="stall" label="Stall" />
                  <AdditionalChartsSection showId={showId} type="stall" />
                </div>
                <div>
                  <ChartBuilder showId={showId} type="rv" label="RV" />
                  <AdditionalChartsSection showId={showId} type="rv" />
                </div>
              </div>
              {/* Legacy Extra Locations editor kept but hidden by default.
                  Enable via ?legacyExtraLocations=1 query param only if a
                  producer needs the old combined view. */}
              {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('legacyExtraLocations') === '1' && (
                <ExtraLocationsEditor showId={showId} />
              )}
            </div>
          </CollapsibleSection>

          {/* Classes */}
          <CollapsibleSection
            id="classes"
            title="Classes"
            badge={<span className="px-2 py-0.5 bg-[#0096FF]/15 text-[#0096FF] rounded text-[10px] font-bold">{classes.length}</span>}
            defaultOpen
          >
            <div className="flex justify-end mb-4">
              <button type="button" data-testid="edit-add-class-btn" onClick={() => addClass()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors text-sm">
                <Plus size={16} weight="bold" /> {isMultiDay && activeEditDayTab !== 'ALL' && activeEditDayTab !== 'UNSCHEDULED' ? `Add to ${activeEditDayTab}` : 'Add Class'}
              </button>
            </div>
            <div className="space-y-4">
              {/* Day Tabs for multi-day shows */}
              {isMultiDay && classes.length > 0 && (() => {
                const usedDays = [...new Set(classes.map(c => c.scheduled_day).filter(Boolean))];
                const hasUnscheduled = classes.some(c => !c.scheduled_day);
                const hasTabs = usedDays.length > 0;
                if (!hasTabs) return null;
                return (
                  <div className="flex gap-1 mb-2 bg-[#0A0A0A] rounded-lg p-1 overflow-x-auto" data-testid="edit-day-tabs">
                    <button type="button" onClick={() => setActiveEditDayTab('ALL')}
                      className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                        activeEditDayTab === 'ALL' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                      }`} data-testid="edit-day-tab-all">
                      All <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                        activeEditDayTab === 'ALL' ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                      }`}>{classes.length}</span>
                    </button>
                    {showDays.map(d => {
                      const count = classes.filter(c => c.scheduled_day === d.value).length;
                      if (count === 0 && !usedDays.includes(d.value)) return null;
                      const dayDate = d.date || '';
                      const isLocked = dayDate && (formData.locked_days || []).includes(dayDate);
                      const toggleLock = (e) => {
                        e.stopPropagation();
                        if (!dayDate) { toast.error('This day has no date — set start/end dates first'); return; }
                        const cur = formData.locked_days || [];
                        setFormData({
                          ...formData,
                          locked_days: isLocked ? cur.filter(x => x !== dayDate) : [...cur, dayDate],
                        });
                        toast.success(isLocked ? `${d.value} unlocked` : `${d.value} locked — rider self-entry blocked`);
                      };
                      return (
                        <div key={d.value} className="flex-shrink-0 relative group">
                          <button type="button" onClick={() => setActiveEditDayTab(d.value)}
                            className={`px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                              activeEditDayTab === d.value ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                            } ${isLocked ? 'pr-9' : ''}`} data-testid={`edit-day-tab-${d.value}`}>
                            {d.value} <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                              activeEditDayTab === d.value ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                            }`}>{count}</span>
                            {isLocked && (
                              <span className="ml-2 text-[10px] font-bold text-[#FACC15]" title="Day locked — rider self-entry blocked">🔒</span>
                            )}
                          </button>
                          {dayDate && (
                            <button
                              type="button"
                              onClick={toggleLock}
                              className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-[10px] font-bold transition-all border ${
                                isLocked
                                  ? 'bg-[#FACC15] text-black border-[#FACC15]'
                                  : 'bg-[#0A0A0A] text-[#9E9E9E] border-[#262626] hover:border-[#FACC15] hover:text-[#FACC15] opacity-0 group-hover:opacity-100'
                              }`}
                              title={isLocked ? `Unlock ${d.value} entries` : `Lock ${d.value} — block rider self-entry (e.g. finals day)`}
                              data-testid={`edit-day-lock-${d.value}`}
                            >
                              {isLocked ? '🔒' : '🔓'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {hasUnscheduled && (
                      <button type="button" onClick={() => setActiveEditDayTab('UNSCHEDULED')}
                        className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                          activeEditDayTab === 'UNSCHEDULED' ? 'bg-[#FF9500] text-white' : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                        }`} data-testid="edit-day-tab-unscheduled">
                        Unassigned <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                          activeEditDayTab === 'UNSCHEDULED' ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                        }`}>{classes.filter(c => !c.scheduled_day).length}</span>
                      </button>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const visibleIds = classes
                  .map((cls, i) => ({ cls, i }))
                  .filter(({ cls }) => {
                    if (!isMultiDay || activeEditDayTab === 'ALL') return true;
                    if (activeEditDayTab === 'UNSCHEDULED') return !cls.scheduled_day;
                    return cls.scheduled_day === activeEditDayTab;
                  })
                  .map(({ cls, i }) => getClassDndId(cls, i));
                return (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
              {classes.map((cls, index) => {
                // Filter by active day tab
                if (isMultiDay && activeEditDayTab !== 'ALL') {
                  if (activeEditDayTab === 'UNSCHEDULED') {
                    if (cls.scheduled_day) return null;
                  } else {
                    if (cls.scheduled_day !== activeEditDayTab) return null;
                  }
                }
                const dndId = getClassDndId(cls, index);
                return (
                <SortableClassCard id={dndId} key={dndId}>
                  {(listeners) => (
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4" data-testid={`edit-class-${index}`}>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-[#525252] hover:text-[#9E9E9E] transition-colors touch-none" data-testid={`edit-class-drag-handle-${index}`} title="Drag to reorder">
                        <DotsSixVertical size={20} weight="bold" />
                      </div>
                      <h3 className="text-white font-semibold">Class {index + 1}</h3>
                      {cls.id && <span className="text-[#525252] text-[10px] font-mono">ID: {cls.id.slice(0, 8)}</span>}
                      {cls.scheduled_day && <span className="px-3 py-1 bg-[#0096FF]/20 text-[#0096FF] rounded-full text-xs font-semibold">{cls.scheduled_day}</span>}
                      {/* v227 — Points mode preview chip */}
                      {(() => {
                        const mode = cls.points_mode || (cls.points_enabled === false ? 'OFF' : 'DIVISIONS');
                        if (mode === 'OFF') return (
                          <span className="px-2 py-1 bg-[#525252]/20 text-[#9E9E9E] rounded-full text-[10px] font-bold uppercase" data-testid={`class-points-chip-${index}`} title="This class doesn't award org points">
                            No Pts
                          </span>
                        );
                        if (mode === 'OVERALL') return (
                          <span className="px-2 py-1 bg-[#00D4FF]/15 text-[#00D4FF] rounded-full text-[10px] font-bold uppercase" data-testid={`class-points-chip-${index}`} title="Overall placings — no division split">
                            Overall
                          </span>
                        );
                        return (
                          <span className="px-2 py-1 bg-[#FF9500]/15 text-[#FF9500] rounded-full text-[10px] font-bold uppercase" data-testid={`class-points-chip-${index}`} title="Split by the org's division format">
                            Divisions
                          </span>
                        );
                      })()}
                    </div>
                    {classes.length > 1 && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => duplicateClass(index)}
                          className="px-2 py-1 text-[#0096FF] hover:text-[#00D4FF] hover:bg-[#0096FF]/10 rounded text-xs font-semibold transition-colors"
                          data-testid={`edit-duplicate-class-${index}`}
                          title="Duplicate this class — keeps fees, payouts, division settings"
                        >
                          Duplicate
                        </button>
                        <button type="button" onClick={() => removeClass(index)} className="text-[#FF3B30] hover:text-[#FF3B30]/80 px-1" data-testid={`edit-remove-class-${index}`}>
                          <Trash size={20} />
                        </button>
                      </div>
                    )}
                    {classes.length === 1 && (
                      <button
                        type="button"
                        onClick={() => duplicateClass(index)}
                        className="px-2 py-1 text-[#0096FF] hover:text-[#00D4FF] hover:bg-[#0096FF]/10 rounded text-xs font-semibold transition-colors"
                        data-testid={`edit-duplicate-class-${index}`}
                        title="Duplicate this class — keeps fees, payouts, division settings"
                      >
                        Duplicate
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Class Name</label>
                      <input type="text" required value={cls.name} data-testid={`edit-class-name-${index}`}
                        onChange={(e) => updateClass(index, 'name', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                        placeholder="e.g., Open 4D Barrels" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Entry Fee ($)</label>
                      <input type="number" required step="0.01" value={cls.entry_fee} data-testid={`edit-class-fee-${index}`}
                        onChange={(e) => updateClass(index, 'entry_fee', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Payback %</label>
                      <input type="number" required value={cls.payback_percent} data-testid={`edit-class-payback-${index}`}
                        onChange={(e) => updateClass(index, 'payback_percent', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Added Money ($)</label>
                      <input type="number" step="0.01" value={cls.added_money} data-testid={`edit-class-added-money-${index}`}
                        onChange={(e) => updateClass(index, 'added_money', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Arena</label>
                      <select value={cls.arena || formData.arenas[0] || ''} data-testid={`edit-class-arena-${index}`}
                        onChange={(e) => updateClass(index, 'arena', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors">
                        {formData.arenas.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Class Type</label>
                      <select
                        value={cls.class_type || 'standard'}
                        onChange={(e) => updateClass(index, 'class_type', e.target.value)}
                        data-testid={`edit-class-type-${index}`}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      >
                        <option value="standard">Standard (with payouts &amp; placings)</option>
                        <option value="warmup">Warm-up (no payout, unlimited entries)</option>
                        <option value="exhibition">Exhibition (no payout, unlimited entries)</option>
                      </select>
                      <p className="text-xs text-[#9E9E9E] mt-1">
                        Warm-up &amp; exhibition classes skip duplicate-entry checks and entry caps.
                      </p>
                    </div>
                    {/* Multi-Go grouping — classes that share a Group + have different
                        Go #s are auto-linked. A rider entering ANY Go pays once and
                        is auto-entered in every other Go in the same group. Mark the
                        Finals class to exclude it from auto-entry (Finals admission
                        happens via the qualification flow, not the rider entry form). */}
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Multi-Go Group <span className="text-[#9E9E9E] text-xs font-normal">(Optional — e.g. "Open Barrels")</span></label>
                      <input
                        type="text"
                        value={cls.multi_go_group || ''}
                        onChange={(e) => updateClass(index, 'multi_go_group', e.target.value)}
                        data-testid={`edit-class-multigo-group-${index}`}
                        placeholder='Leave blank for normal class'
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FF9500] transition-colors"
                      />
                      <p className="text-xs text-[#9E9E9E] mt-1">Classes sharing a group are linked into a Multi-Go (rider charged once, auto-entered in every Go).</p>
                    </div>
                    {(cls.multi_go_group || '').trim() && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Go #</label>
                          <select
                            value={cls.go_number ?? ''}
                            onChange={(e) => updateClass(index, 'go_number', e.target.value ? parseInt(e.target.value, 10) : null)}
                            data-testid={`edit-class-go-number-${index}`}
                            className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FF9500] transition-colors"
                          >
                            <option value="">—</option>
                            <option value="1">1st Go</option>
                            <option value="2">2nd Go</option>
                            <option value="3">3rd Go</option>
                            <option value="4">4th Go</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Role</label>
                          <button
                            type="button"
                            onClick={() => updateClass(index, 'is_finals', !cls.is_finals)}
                            data-testid={`edit-class-is-finals-${index}`}
                            className={`w-full px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                              cls.is_finals
                                ? 'bg-[#FACC15]/15 border border-[#FACC15] text-[#FACC15]'
                                : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:border-[#FF9500]/50'
                            }`}
                          >
                            {cls.is_finals ? '★ Finals Class' : 'Mark as Finals'}
                          </button>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Org Points</label>
                          <select
                            value={cls.points_mode || 'DIVISIONS'}
                            onChange={(e) => updateClass(index, 'points_mode', e.target.value)}
                            data-testid={`edit-class-points-mode-${index}`}
                            title="How this class's placings feed into the organization's season standings."
                            className="w-full px-3 py-2 rounded-md text-sm font-semibold bg-[#161616] border border-[#262626] text-white hover:border-[#FF9500]/50"
                          >
                            <option value="DIVISIONS">Split by Divisions</option>
                            <option value="OVERALL">Overall Placings</option>
                            <option value="OFF">No Points</option>
                          </select>
                        </div>
                        {cls.is_finals && (
                          <div className="col-span-2 bg-[#FACC15]/5 border border-[#FACC15]/20 rounded-md p-3">
                            <label className="block text-sm font-semibold text-[#FACC15] mb-2">
                              ★ Qualification Rule (per-Go top-N)
                            </label>
                            <div className="flex items-center gap-3">
                              <input
                                type="number"
                                min="1"
                                max="50"
                                value={(cls.qualification_rule || {}).top_n || ''}
                                onChange={(e) => updateClass(index, 'qualification_rule', {
                                  ...(cls.qualification_rule || {}),
                                  top_n: parseInt(e.target.value, 10) || 0,
                                  type: 'per_go',
                                })}
                                data-testid={`edit-finals-top-n-${index}`}
                                placeholder="10"
                                className="w-24 px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FACC15]"
                              />
                              <p className="text-xs text-[#FACC15]">
                                Top N from EACH Go in this group qualify. Duplicates (same rider already qualified from earlier Go) auto-bumped to next rider.
                              </p>
                            </div>
                            {/* v245 — Optional per-division scoping. When the
                                source Gos are 2D/3D/4D classes, producers can
                                pick "top N per division" so each division gets
                                its own N qualifiers rather than N overall. */}
                            <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid={`edit-finals-per-division-label-${index}`}>
                              <input
                                type="checkbox"
                                checked={!!(cls.qualification_rule || {}).per_division}
                                onChange={(e) => updateClass(index, 'qualification_rule', {
                                  ...(cls.qualification_rule || {}),
                                  per_division: e.target.checked,
                                  type: 'per_go',
                                })}
                                data-testid={`edit-finals-per-division-${index}`}
                                className="mt-0.5 accent-[#FACC15]"
                              />
                              <div>
                                <p className="text-xs font-semibold text-white">Top N per Division</p>
                                <p className="text-[11px] text-[#9E9E9E]">
                                  Take top N from EACH division (1D, 2D, 3D, 4D) per Go, not top N overall. Requires source Gos to have a Division Type set.
                                </p>
                              </div>
                            </label>
                          </div>
                        )}
                      </>
                    )}
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                        Entry Cap <span className="text-[#9E9E9E] text-xs font-normal">(Optional — leave blank for no limit)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={cls.entry_cap || ''}
                        onChange={(e) => updateClass(index, 'entry_cap', e.target.value)}
                        data-testid={`edit-class-entry-cap-${index}`}
                        placeholder="e.g., 50"
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FACC15] transition-colors"
                      />
                      <p className="text-xs text-[#9E9E9E] mt-1">
                        Once this many riders enter, the class is closed to new entries (rollovers still allowed).
                      </p>
                    </div>
                    {/* v248 — Drag size. Announcer's "drag" width — the
                        printed draw sheet leaves a small gap every N
                        entries so the announcer can pause between drags. */}
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                        Drag Size <span className="text-[#9E9E9E] text-xs font-normal">(Optional — 0 or blank for no drag gaps)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={cls.drag_size === 0 || cls.drag_size == null ? '' : cls.drag_size}
                        onChange={(e) => updateClass(index, 'drag_size', e.target.value)}
                        data-testid={`edit-class-drag-size-${index}`}
                        placeholder="e.g., 5"
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                      />
                      <p className="text-xs text-[#9E9E9E] mt-1">
                        On the printed draw sheet, a small gap appears after every N entries — one gap after #{cls.drag_size || 5}, another after #{(parseInt(cls.drag_size, 10) || 5) * 2}, and so on.
                      </p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Division Type</label>
                      <select value={cls.division_type || 'NONE'} data-testid={`edit-class-division-${index}`}
                        onChange={(e) => updateClass(index, 'division_type', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors">
                        <option value="NONE">Not Set</option>
                        <option value="4D">4D (0.5s, 1.0s, 2.0s splits)</option>
                        <option value="3D">3D (1.0s, 2.0s splits)</option>
                        <option value="2D">2D (Custom split)</option>
                        <option value="OVERALL">No Divisions (Overall Only)</option>
                      </select>
                    </div>
                    {cls.division_type === '2D' && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">2D Split Time</label>
                        <select value={cls.division_split || 0.5}
                          onChange={(e) => updateClass(index, 'division_split', parseFloat(e.target.value))}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors">
                          <option value="0.5">0.5 seconds</option>
                          <option value="1.0">1.0 seconds</option>
                        </select>
                      </div>
                    )}
                    {(cls.division_type === '3D' || cls.division_type === '4D') && (() => {
                      const divNames = cls.division_type === '4D' ? ['1D','2D','3D','4D'] : ['1D','2D','3D'];
                      const defaults = cls.division_type === '4D' ? [0, 0.5, 1.0, 2.0] : [0, 1.0, 2.0];
                      const splits = Array.isArray(cls.division_splits) && cls.division_splits.length === divNames.length
                        ? cls.division_splits
                        : defaults;
                      return (
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Division Time Splits</label>
                          <p className="text-xs text-[#9E9E9E] mb-2">Seconds behind the fastest time for each division. 1D is always 0.</p>
                          <div className={`grid ${cls.division_type === '4D' ? 'grid-cols-4' : 'grid-cols-3'} gap-2`}>
                            {divNames.map((divName, idx) => (
                              <div key={divName}>
                                <label className="block text-[10px] text-[#9E9E9E] mb-1">{divName}</label>
                                <div className="relative">
                                  <input
                                    type="number" min="0" step="0.1"
                                    value={idx === 0 ? 0 : (splits[idx] ?? 0)}
                                    disabled={idx === 0}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value);
                                      const next = [...splits];
                                      while (next.length < divNames.length) next.push(0);
                                      next[idx] = isNaN(val) ? 0 : val;
                                      next[0] = 0;
                                      updateClass(index, 'division_splits', next);
                                    }}
                                    className={`w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-sm text-right pr-8 focus:outline-none focus:border-[#0096FF] ${idx === 0 ? 'text-[#555] cursor-not-allowed' : 'text-white'}`}
                                    data-testid={`edit-class-split-${index}-${divName.toLowerCase()}`}
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#555] text-[10px]">sec</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {isMultiDay && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Scheduled Day</label>
                        <select value={cls.scheduled_day} data-testid={`edit-class-day-${index}`}
                          onChange={(e) => updateClass(index, 'scheduled_day', e.target.value)}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors">
                          <option value="">Select Day</option>
                          {showDays.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                      </div>
                    )}
                    {/* Rollover Configuration */}
                    <div className="col-span-2 border-t border-[#262626] pt-3 mt-1">
                      <label className="block text-sm font-medium text-[#FF9500] mb-2">Rollover From (Optional)</label>
                      <p className="text-[10px] text-[#555] mb-2">Select one or more source classes whose entries roll into this class</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid={`edit-class-rollover-from-${index}`}>
                        {classes.filter((_, i) => i !== index).map(c => {
                          const cId = c.id;
                          const selected = (cls.rollover_from_class_ids || []).includes(cId);
                          return (
                            <label key={cId || index} className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${selected ? 'bg-[#FF9500]/10 border border-[#FF9500]/30' : 'bg-[#161616] border border-[#262626] hover:border-[#FF9500]/20'}`}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => {
                                  const current = cls.rollover_from_class_ids || [];
                                  const next = selected ? current.filter(id => id !== cId) : [...current, cId];
                                  updateClass(index, 'rollover_from_class_ids', next);
                                }}
                                className="accent-[#FF9500]"
                                data-testid={`edit-class-rollover-check-${index}-${cId}`}
                              />
                              <span className="text-white text-sm">{c.name || `Class ${classes.indexOf(c) + 1}`}</span>
                            </label>
                          );
                        })}
                      </div>
                      {(cls.rollover_from_class_ids || []).length > 0 && (
                        <div className="mt-2">
                          <select
                            value={cls.rollover_category || ''}
                            onChange={(e) => updateClass(index, 'rollover_category', e.target.value)}
                            className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FF9500] transition-colors"
                            data-testid={`edit-class-rollover-category-${index}`}
                          >
                            <option value="">All Entries</option>
                            <option value="YOUTH">Youth Only</option>
                            <option value="ADULT">Adult Only</option>
                            <option value="MASTERS">Masters Only</option>
                          </select>
                        </div>
                      )}
                    </div>
                    {/* v261 — Base pool on another class's entry count. Producers
                        use this for finals classes where the money was already
                        collected in Go 1 / Go 2 entries. Pool = entry_fee ×
                        N(source class paid entries) × payback% + added_money. */}
                    <div className="col-span-2 border-t border-[#262626] pt-3 mt-1">
                      <label className="block text-sm font-medium text-[#FFCC00] mb-2">Base Payout Pool On (Optional)</label>
                      <p className="text-[10px] text-[#555] mb-2">
                        Use another class's paid entry count to size this pool. Typical use: a finals class funded
                        by Go 1 / Go 2 entry fees — point this at Go 1 so the pool is
                        <code className="text-[#FFCC00] px-1">entry_fee × N(Go 1) × payback% + added_money</code>.
                      </p>
                      <select
                        value={cls.entry_count_source_class_id || ''}
                        onChange={(e) => updateClass(index, 'entry_count_source_class_id', e.target.value)}
                        className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FFCC00] transition-colors"
                        data-testid={`edit-class-pool-source-${index}`}
                      >
                        <option value="">Use this class's own entries (default)</option>
                        {classes.filter((_, i) => i !== index && classes[i].id).map(c => (
                          <option key={c.id} value={c.id}>{c.name || 'Untitled class'}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                  )}
                </SortableClassCard>
                );
              })}
                  </SortableContext>
                </DndContext>
                );
              })()}
            </div>
          </CollapsibleSection>

          {/* Sidepots */}
          <CollapsibleSection
            id="sidepots"
            title="Sidepots"
            badge={<span className="px-2 py-0.5 bg-[#FF9500]/15 text-[#FF9500] rounded text-[10px] font-bold">{sidepots.length}</span>}
            accent="#FF9500"
            defaultOpen={false}
          >
            <div className="flex justify-end mb-4">
              <button type="button" data-testid="edit-add-sidepot-btn" onClick={addSidepot}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors text-sm">
                <Plus size={16} weight="bold" /> Add Sidepot
              </button>
            </div>
            {sidepots.length === 0 ? (
              <p className="text-[#9E9E9E] text-sm">No sidepots configured. Add sidepots for additional rider buy-ins.</p>
            ) : (
              <div className="space-y-3">
                {sidepots.map((sp, index) => (
                  <div key={sp.id || index} className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4" data-testid={`edit-sidepot-${index}`}>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-white font-semibold">Sidepot {index + 1}</h3>
                      <button type="button" onClick={() => removeSidepot(index)} className="text-[#FF3B30] hover:text-[#FF3B30]/80">
                        <Trash size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Name</label>
                        <input type="text" required value={sp.name} data-testid={`edit-sidepot-name-${index}`}
                          onChange={(e) => updateSidepot(index, 'name', e.target.value)}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                          placeholder="e.g., Youth Sidepot" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Fee ($)</label>
                        <input type="number" required step="0.01" value={sp.entry_fee} data-testid={`edit-sidepot-fee-${index}`}
                          onChange={(e) => updateSidepot(index, 'entry_fee', e.target.value)}
                          className="w-full px-4 py-2 bg-[#161616] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Rolls Over From</label>
                        <div
                          className="flex flex-wrap gap-1.5 px-3 py-2 bg-[#161616] border border-[#262626] rounded-md min-h-[42px]"
                          data-testid={`edit-sidepot-rollover-${index}`}
                        >
                          {classes.length === 0 ? (
                            <span className="text-xs text-[#555]">Add a class first</span>
                          ) : classes.map((c, ci) => {
                            const cid = c.id;
                            if (!cid || !c.name) return null;
                            const selected = (sp.rollover_from_class_ids || []).includes(cid);
                            return (
                              <button
                                key={cid}
                                type="button"
                                onClick={() => {
                                  const cur = new Set(sp.rollover_from_class_ids || []);
                                  if (cur.has(cid)) cur.delete(cid); else cur.add(cid);
                                  updateSidepot(index, 'rollover_from_class_ids', Array.from(cur));
                                }}
                                className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                                  selected
                                    ? 'bg-[#0096FF] text-white'
                                    : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white'
                                }`}
                                data-testid={`edit-sidepot-${index}-class-toggle-${ci}`}
                              >
                                {c.name}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-[#555] mt-1">Riders entering any selected class can opt in to this sidepot.</p>
                      </div>
                    </div>

                    {/* Advanced sidepot config — mirrors classes */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-[#262626]">
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Division Type</label>
                        <select
                          value={sp.division_type || 'NONE'}
                          onChange={(e) => updateSidepot(index, 'division_type', e.target.value)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`edit-sidepot-division-type-${index}`}
                        >
                          <option value="NONE">None (Overall)</option>
                          <option value="2D">2D</option>
                          <option value="3D">3D</option>
                          <option value="4D">4D</option>
                        </select>
                      </div>
                      {sp.division_type === '2D' && (
                        <div>
                          <label className="block text-xs font-medium text-[#F8F8F8] mb-1">2D Split (sec)</label>
                          <input type="number" step="0.01" min="0"
                            value={sp.division_split ?? 0.5}
                            onChange={(e) => updateSidepot(index, 'division_split', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                            data-testid={`edit-sidepot-2d-split-${index}`} placeholder="0.5" />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Payout %</label>
                        <input type="number" step="1" min="0" max="100"
                          value={sp.payout_pct ?? 100}
                          onChange={(e) => updateSidepot(index, 'payout_pct', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`edit-sidepot-payout-pct-${index}`} placeholder="100" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Added Money ($)</label>
                        <input type="number" step="1" min="0"
                          value={sp.added_money ?? 0}
                          onChange={(e) => updateSidepot(index, 'added_money', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`edit-sidepot-added-money-${index}`} placeholder="0" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Scheduled Day</label>
                        <select
                          value={sp.scheduled_day || ''}
                          onChange={(e) => updateSidepot(index, 'scheduled_day', e.target.value)}
                          className="w-full px-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
                          data-testid={`edit-sidepot-scheduled-day-${index}`}
                        >
                          <option value="">Any day</option>
                          {['Friday','Saturday','Sunday','Monday','Tuesday','Wednesday','Thursday'].map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>

          {/* Video Stream */}
          <CollapsibleSection id="video" title="Live Stream" defaultOpen={false}>
            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Live Stream URL (Optional)</label>
              <input data-testid="edit-video-url" type="url" value={formData.video_stream_url}
                onChange={(e) => setFormData({...formData, video_stream_url: e.target.value})}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white placeholder-[#525252] focus:outline-none focus:border-[#0096FF] transition-colors"
                placeholder="https://www.youtube.com/embed/VIDEO_ID" />
            </div>
          </CollapsibleSection>

          {/* Custom Entry Form + text customizations — for the mail-in
              path (v208 auto-generated form). Producer can either
              upload their own polished PDF that overrides ours, or
              tweak the header/footer/release blocks that live in our
              auto-generated form. */}
          <CollapsibleSection id="entry-form-customization" title="Printable Entry Form"
            subtitle="Customize the mail-in / printable entry form for this show" defaultOpen={false}>
            <div className="space-y-4">
              <div className="p-3 bg-[#0A0A0A] border border-[#262626] rounded-md">
                <label className="block text-sm font-semibold text-white mb-2">
                  Upload Your Own Entry Form (optional)
                </label>
                <p className="text-[11px] text-[#9E9E9E] mb-3">
                  Have a polished PDF or scanned form? Upload it and it replaces the auto-generated
                  version. Accepted: PDF, JPG, PNG, WebP · max 15&nbsp;MB.
                </p>
                {hasCustomEntryForm ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/shows/${showId}/custom-entry-form-file`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30 rounded text-xs font-semibold hover:bg-[#34C759]/25 transition-colors"
                      data-testid="preview-custom-entry-form"
                    >
                      ✓ Custom file uploaded — preview
                    </a>
                    <button
                      type="button"
                      onClick={handleCustomEntryFormDelete}
                      className="px-3 py-1.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30 rounded text-xs font-semibold hover:bg-[#FF3B30]/25 transition-colors"
                      data-testid="remove-custom-entry-form-btn"
                    >
                      Remove
                    </button>
                    <label className="px-3 py-1.5 bg-transparent text-[#0096FF] border border-[#0096FF]/30 rounded text-xs font-semibold hover:bg-[#0096FF]/10 transition-colors cursor-pointer">
                      Replace
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleCustomEntryFormUpload} className="hidden" data-testid="custom-entry-form-replace-input" />
                    </label>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30 rounded-md text-sm font-semibold hover:bg-[#0096FF]/25 transition-colors cursor-pointer">
                    {uploadingEntryForm ? 'Uploading...' : 'Upload Entry Form'}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={handleCustomEntryFormUpload}
                      disabled={uploadingEntryForm}
                      className="hidden"
                      data-testid="custom-entry-form-upload-input"
                    />
                  </label>
                )}
              </div>

              <div className={hasCustomEntryForm ? 'opacity-40 pointer-events-none' : ''}>
                <p className="text-[11px] text-[#9E9E9E] mb-3">
                  {hasCustomEntryForm
                    ? 'These fields are ignored while a custom file is uploaded.'
                    : 'These text blocks appear inside the auto-generated entry form.'}
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                    Header Note <span className="text-[#9E9E9E] font-normal">(short — e.g., &ldquo;Late fees apply after Mar 15&rdquo;)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={formData.entry_form_header_note}
                    onChange={(e) => setFormData({...formData, entry_form_header_note: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors resize-vertical"
                    data-testid="edit-entry-form-header-note"
                    placeholder="Optional — highlighted at the top of the form"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                    Release / Waiver Text <span className="text-[#9E9E9E] font-normal">(small print, above signature line)</span>
                  </label>
                  <textarea
                    rows={4}
                    value={formData.entry_form_release_text}
                    onChange={(e) => setFormData({...formData, entry_form_release_text: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors resize-vertical"
                    data-testid="edit-entry-form-release-text"
                    placeholder='Optional — e.g., "I hereby release the producer from all liability..."'
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#F8F8F8] mb-2">
                    Footer Note <span className="text-[#9E9E9E] font-normal">(refund policy, contact reminder, etc.)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={formData.entry_form_footer_note}
                    onChange={(e) => setFormData({...formData, entry_form_footer_note: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors resize-vertical"
                    data-testid="edit-entry-form-footer-note"
                    placeholder="Optional — small italic block below the signature line"
                  />
                </div>
              </div>

              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/shows/${showId}/printable-entry-form.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 rounded-md text-sm font-semibold hover:bg-[#FF9500]/25 transition-colors"
                data-testid="preview-entry-form-btn"
              >
                Preview Final Entry Form
              </a>
            </div>
          </CollapsibleSection>

          {/* Co-Sanctioning Organizations — v217. Producer proposes other
              orgs sanction this show so it counts toward their point
              standings. Target org admin must approve. */}
          <CollapsibleSection
            id="co-sanctioning"
            title="Co-Sanctioning Organizations"
            subtitle="Let other associations count this show toward their point standings"
            defaultOpen={false}
          >
            <CoSanctioningSection showId={showId} ownerOrgId={ownerOrgId} />
          </CollapsibleSection>

          {/* Submit */}
          <div className="flex gap-4">
            <button type="button" onClick={() => navigate(`/shows/${showId}`)}
              className="px-6 py-3 bg-transparent border border-[#262626] hover:border-[#0096FF] text-white rounded-md transition-colors" data-testid="edit-cancel-btn">
              Cancel
            </button>
            {/* v227 — Duplicate a show. One-click clone of classes,
                sidepots, stalls/RV, pricing, and add-ons; entries and
                scores are NOT copied. */}
            <button
              type="button"
              onClick={async () => {
                const proposed = window.prompt(
                  'Duplicate this show?\n\nEnter a name for the copy:',
                  `${formData?.name || 'Show'} (Copy)`
                );
                if (!proposed || !proposed.trim()) return;
                const startDate = window.prompt(
                  'Start date for the new show (YYYY-MM-DD, optional):', ''
                ) || '';
                try {
                  const { data } = await api.post(`/shows/${showId}/duplicate`, {
                    name: proposed.trim(),
                    start_date: startDate.trim(),
                  });
                  toast.success('Show duplicated');
                  navigate(`/shows/${data.id}/edit`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Duplicate failed');
                }
              }}
              className="px-6 py-3 bg-transparent border border-[#00D4FF]/40 hover:border-[#00D4FF] text-[#00D4FF] rounded-md transition-colors font-semibold"
              data-testid="edit-duplicate-show-btn"
              title="Create a new show pre-populated with this show's setup"
            >
              Duplicate Show
            </button>
            <button data-testid="edit-save-btn" type="submit" disabled={saving}
              className="flex-1 px-6 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving Changes...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Floating Add Class Button */}
      <button
        type="button"
        onClick={() => addClass()}
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 px-5 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-bold rounded-full shadow-lg shadow-[#0096FF]/30 transition-all hover:scale-105"
        data-testid="edit-floating-add-class-btn"
      >
        <Plus size={18} weight="bold" /> Add Class
      </button>
    </div>
  );
};

export default ShowEdit;
